// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TimeSpan, TimeSpanPeriods, TimeSpanFormat } from '@shared/utils/Time/TimeSpan';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { ChartDataType } from '@front/chart/Utils/ChartConstants';
import { ErrorInformationStorage } from '../../ErrorInformationStorage';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { HistorySortOrder } from '@shared/utils/History/ReloadHistoryParams';
import { Periods } from '@shared/utils/History/TFInfo';
import { Message } from '@shared/utils/DirectMessages/DirectMessagesImport';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { BaseInterval } from '@shared/utils/History/BaseInterval';
import { BaseIntervalInputParams } from '@shared/utils/History/BaseIntervalInputParams';
import { BarIndex, NOT_FOUND_INTERVAL_VALUE } from './HistoryConstants';
import { SessionPeriod } from '@shared/utils/Session/SessionPeriod';
import { HistoryManager } from './HistoryManager';
import { CashItemQuoteCache } from '../CashItemQuoteCache';
//
// Хранение истории. Аналог кешитема в клиенте
//
export class CashItem {
    get StartTime() {
        return this.FStartTime;
    }
    get onlyMainSession() {
        var _a, _b;
        return (_b = (_a = this.SessionInfo) === null || _a === void 0 ? void 0 : _a.OnlyMainSession) !== null && _b !== void 0 ? _b : false;
    }
    get isFilterNotAllowedPeriods() {
        const currentAccount = this.Account;
        if (this.isIgnoreAccount || isNullOrUndefined(currentAccount))
            return !this.Instrument.isAllowOTHTrading;
        return !currentAccount.isAllowOTHTrading(this.Instrument);
    }
    // private SyncronizedSessionFlag: boolean;
    constructor(account, symbol, parent, timeFrameInfo, isIgnoreAccount) {
        this.lastLoadedBarsCount = 0;
        this._isMaxHistoryLoaded = false;
        this.quoteCache = new CashItemQuoteCache();
        this.Account = account;
        this.isIgnoreAccount = isIgnoreAccount;
        this.FSymbol = symbol;
        this.Instrument = parent.DataCache.getInstrumentByName(this.FSymbol);
        this.TimeFrameInfo = timeFrameInfo;
        this.FPeriod = timeFrameInfo.Periods;
        this.ChartDataType = timeFrameInfo.ChartDataType;
        this.AdditionalKey = timeFrameInfo.AdditionalKey;
        this.Plan = timeFrameInfo.Plan;
        this.HistoryType = timeFrameInfo.HistoryType;
        const sesInfo = timeFrameInfo.SessionInfo;
        this.SessionInfo = sesInfo;
        this.Parent = parent;
        // Здесь лежат бары
        this.FNonEmptyCashArray = [];
        /// <summary>
        /// Для быстрого доступа к размеру FNonEmptyCashArray (необходимо всегда синхронизировать)
        /// </summary>
        this.FNonEmptyCashArrayCount = 0;
        /// <summary>
        /// Стартовое время истории
        /// </summary>
        this.FStartTime = 0;
        this.HistoryExpanded = new CustomEvent();
        this.HistoryReload = new CustomEvent();
        this.QuoteProcessed = new CustomEvent();
        /// //////////////////////////////////////////
        this.spreadItem = !isNullOrUndefined(this.Plan) ? this.Plan.GetItem(this.Instrument) : null;
        this.FFuncList = [];
        this.collectTickQuotes = true;
        // this.SyncronizedSessionFlag = null;     // #100139
    }
    getType() {
        return 'CASH_ITEM_CONTROLLER';
    }
    get isRealTimeBuilding() {
        return this._isRealTimeBuilding;
    }
    set isRealTimeBuilding(value) {
        const oldValue = this._isRealTimeBuilding;
        this._isRealTimeBuilding = value;
        if (value && !oldValue) {
            this.applyQuotes();
        }
    }
    get realTimeBar() {
        return this._realTimeBar;
    }
    set realTimeBar(value) {
        this._realTimeBar = value;
    }
    get isMaxHistoryLoaded() {
        return this._isMaxHistoryLoaded;
    }
    set isMaxHistoryLoaded(value) {
        this._isMaxHistoryLoaded = value;
    }
    historyLoaded(history, historyParams, isNeedRealTime = true) {
        this.FNonEmptyCashArray = history;
        this.FNonEmptyCashArrayCount = this.FNonEmptyCashArray.length;
        this.lastLoadedBarsCount = this.FNonEmptyCashArrayCount;
        if (this.FNonEmptyCashArrayCount > 0) {
            this.FStartTime = this.FNonEmptyCashArray[0].FLeftTimeTicks;
        }
        this.collectTickQuotes = false;
        this.isRealTimeBuilding = isNeedRealTime;
        this.updateRealTimeState();
        this.updateMaxLoadedState(historyParams);
        this.HistoryReload.Raise(this);
    }
    InsertPaddingHistory(paddingInvervals, historyParams) {
        const aggregatedByServer = historyParams.ShouldCheckAggregatedPeriods;
        const paddingHistory = HistoryManager.expandAndMergeHistory(paddingInvervals, !aggregatedByServer, historyParams);
        this.lastLoadedBarsCount = paddingHistory.length;
        if (historyParams.sortOrder === HistorySortOrder.Descending) {
            this.FNonEmptyCashArray.unshift(...paddingHistory);
        }
        else {
            this.FNonEmptyCashArray.push(...paddingHistory);
        }
        this.FNonEmptyCashArrayCount = this.FNonEmptyCashArray.length;
        if (this.FNonEmptyCashArrayCount > 0) {
            this.FStartTime = this.FNonEmptyCashArray[0].FLeftTimeTicks;
            this.updateRealTimeState();
        }
        this.updateMaxLoadedState(historyParams);
    }
    GetByType(index, priceType) {
        if (index >= 0 && index < this.FNonEmptyCashArray.length) {
            const bi = this.FNonEmptyCashArray[index];
            if (this.Period === Periods.TIC && this.ChartDataType === ChartDataType.Default) {
                return bi.Data[priceType === PriceType.Close ? PriceType.Close : PriceType.Open];
            }
            if (priceType < 4) {
                return bi.Data[priceType];
            }
            if (priceType === PriceType.Medium) {
                return bi.Median;
            }
            else if (priceType === PriceType.Typical) {
                return bi.Typical;
            }
            else if (priceType === PriceType.Weighted) {
                return bi.Weighted;
            }
            else {
                return 0;
            }
        }
        else {
            return 0;
        }
    }
    GetByConst(index, priceType) {
        if (index >= 0 && index < this.FNonEmptyCashArray.length) {
            const bi = this.FNonEmptyCashArray[index];
            // if (FPeriod == 0 && ChartDataType == cash.ChartDataType.Default)
            //    return bi.Data[priceType == PriceType.Close ? 1 : 0];
            // if (priceType < 4)
            if (priceType === BarIndex.Time) {
                return bi.FLeftTimeTicks;
            }
            if (priceType === BarIndex.TimeClose) {
                return bi.FRightTimeTicks;
            }
            switch (priceType) {
                case BarIndex.Open:
                    return bi.Data[BaseInterval.OPEN_INDEX];
                case BarIndex.Close:
                    return bi.Data[BaseInterval.CLOSE_INDEX];
                case BarIndex.High:
                    return bi.Data[BaseInterval.HIGH_INDEX];
                case BarIndex.Low:
                    return bi.Data[BaseInterval.LOW_INDEX];
                case BarIndex.Median:
                    return bi.Median;
                case BarIndex.Typical:
                    return bi.Typical;
                case BarIndex.Weighted:
                    return bi.Weighted;
            }
            if (priceType === BarIndex.Volume) {
                return bi.Volume;
            }
            else if (priceType === PriceType.Typical) {
                return bi.Typical;
            }
            else if (priceType === PriceType.Weighted) {
                return bi.Weighted;
            }
            else {
                return 0;
            }
        }
        else {
            return 0;
        }
    }
    Dispose() {
        this.UnSubscribeCurrentInstrument();
    }
    GetQuoteType() {
        return this.Instrument.GetQuoteType(this.HistoryType);
    }
    SubscribeCurrentInstrument() {
        var _a;
        if ((_a = this.Parent) === null || _a === void 0 ? void 0 : _a.addListener) {
            this.Parent.addListener(this.Instrument, this, this.GetQuoteType());
        }
    }
    UnSubscribeCurrentInstrument() {
        var _a;
        if ((_a = this.Parent) === null || _a === void 0 ? void 0 : _a.removeListener) {
            this.Parent.removeListener(this.Instrument, this, this.GetQuoteType());
        }
    }
    Count() {
        return this.FNonEmptyCashArray.length;
    }
    GetVolume(index) {
        if (index >= 0 && index < this.FNonEmptyCashArrayCount) {
            return this.FNonEmptyCashArray[index].Volume;
        }
        else {
            return 0;
        }
    }
    GetVolumeAndTicks(index) {
        if (index < 0 || index >= this.FNonEmptyCashArrayCount || this.ChartDataType !== ChartDataType.Default) {
            return { volume: null, ticks: null };
        }
        if (this.HistoryType === HistoryType.LAST) {
            if (this.FPeriod === 0) {
                return { ticks: 1, volume: this.FNonEmptyCashArray[index].Volume };
            }
            else {
                const bi = this.FNonEmptyCashArray[index];
                return { ticks: bi.Ticks, volume: bi.Volume };
            }
        }
        else {
            return { ticks: this.FNonEmptyCashArray[index].Volume, volume: null };
        }
    }
    getLastPrice() {
        if (this.isRealTimeBuilding) {
            return this.GetByType(this.Count() - 1, PriceType.Close);
        }
        return this.realTimeBar.Data[PriceType.Close];
    }
    get Symbol() {
        return this.FSymbol;
    }
    get Period() {
        return this.FPeriod;
    }
    /// <summary>
    /// Поиск временного интервала, в который
    /// попадает данное время
    /// </summary>
    FindIntervalByTime(time) {
        return this.FindInterval(time, 0, this.Count() - 1);
    }
    FindNearestIntervalByTime(time) {
        const [index, from, to] = this.FindNearestInterval(time, 0, this.Count() - 1);
        if (index === NOT_FOUND_INTERVAL_VALUE) {
            return from;
        }
        return index;
    }
    FindNearestInterval(time, from, to) {
        if (to - from === 0) {
            const ot = this.GetOpenTime(from);
            const ct = this.GetCloseTime(from);
            if (ot === -1 || ct === -1) {
                return [NOT_FOUND_INTERVAL_VALUE, from, to];
            }
            if (time >= ot && time < ct) {
                return [from, from, to];
            }
            // +++ Для тиков кейс такой, время открытия/закрытия у них одинаковое
            else if (ot === ct && time === ot) {
                return [from, from, to];
            }
            else {
                return [NOT_FOUND_INTERVAL_VALUE, from, to];
            }
        }
        else if (to - from === 1) {
            const ot1 = this.GetOpenTime(from);
            const ct1 = this.GetCloseTime(from);
            const ot2 = this.GetOpenTime(to);
            const ct2 = this.GetCloseTime(to);
            if (ot1 === -1 || ct1 === -1 || ot2 === -1 || ct2 === -1) {
                return [NOT_FOUND_INTERVAL_VALUE, from, to];
            }
            if (time >= ot1 && time < ct1) {
                return [from, from, to];
            }
            else if (time >= ot2 && time < ct2) {
                return [to, from, to];
            }
            else if (ot1 === ct1 && ot2 === ct2 && time >= ot1 && time <= ct2) {
                // +++ Для тиков кейс такой, время открытия/закрытия у них одинаковое
                if (time >= ot2) {
                    return [to, from, to];
                }
                else {
                    return [from, from, to];
                }
            }
            else {
                return [NOT_FOUND_INTERVAL_VALUE, from, to];
            }
        }
        else {
            const middle = Math.floor(from + (to - from) / 2);
            const mt = this.GetOpenTime(middle);
            if (mt === -1) {
                return [NOT_FOUND_INTERVAL_VALUE, from, to];
            }
            if (time === mt) {
                return [middle, from, to];
            }
            else if (time > mt) {
                return this.FindNearestInterval(time, middle, to);
            }
            else {
                return this.FindNearestInterval(time, from, middle);
            }
        }
    }
    findNearestMatchingPeriodTime(time) {
        const barsIndexes = this.FindNearestInterval(time.getTime(), 0, this.Count() - 1);
        let nearestTime = 0;
        let nearestPeriodStart = new Date(0);
        for (const barIndex of barsIndexes) {
            if (barIndex < 0)
                continue;
            const interval = this.GetInterval(barIndex);
            const possibleTime = interval.LeftTime.getTime();
            const currentPeriodStart = this.truncateToPeriodStart(possibleTime);
            if (Math.abs(nearestPeriodStart.getTime() - time.getTime()) >
                Math.abs(time.getTime() - currentPeriodStart.getTime())) {
                nearestTime = possibleTime;
                nearestPeriodStart = currentPeriodStart;
            }
        }
        return nearestTime;
    }
    /// <summary>
    /// Рекурсивный метод поиска делением пополам
    /// </summary>
    /// <param name="time"></param>
    /// <param name="index"></param>
    /// <returns></returns>
    FindInterval(time, from, to) {
        if (to - from === 0) {
            const ot = this.GetOpenTime(from);
            const ct = this.GetCloseTime(from);
            if (ot === -1 || ct === -1) {
                return NOT_FOUND_INTERVAL_VALUE;
            }
            if (time >= ot && time < ct) {
                return from;
            }
            else if (ot === ct && time === ot) {
                // +++ Для тиков кейс такой, время открытия/закрытия у них одинаковое
                return from;
            }
            else {
                return NOT_FOUND_INTERVAL_VALUE;
            }
        }
        else if (to - from === 1) {
            const ot1 = this.GetOpenTime(from);
            const ct1 = this.GetCloseTime(from);
            const ot2 = this.GetOpenTime(to);
            const ct2 = this.GetCloseTime(to);
            if (ot1 === -1 || ct1 === -1 || ot2 === -1 || ct2 === -1) {
                return NOT_FOUND_INTERVAL_VALUE;
            }
            if (time >= ot1 && time < ct1) {
                return from;
            }
            else if (time >= ot2 && time < ct2) {
                return to;
            }
            else if (ot1 === ct1 && ot2 === ct2 && time >= ot1 && time <= ct2) {
                // +++ Для тиков кейс такой, время открытия/закрытия у них одинаковое
                if (time >= ot2) {
                    return to;
                }
                else {
                    return from;
                }
            }
            else {
                return NOT_FOUND_INTERVAL_VALUE;
            }
        }
        else {
            const middle = Math.floor(from + (to - from) / 2);
            const mt = this.GetOpenTime(middle);
            if (mt === -1) {
                return NOT_FOUND_INTERVAL_VALUE;
            }
            if (time === mt) {
                return middle;
            }
            else if (time > mt) {
                return this.FindInterval(time, middle, to);
            }
            else {
                return this.FindInterval(time, from, middle);
            }
        }
    }
    /// <summary>
    /// Получить время начала интервала по индексу
    /// </summary>
    GetOpenTime(index) {
        // Извлекаем данные по новому индексу
        if (index >= 0 && index < this.FNonEmptyCashArrayCount) {
            if (index < this.FNonEmptyCashArrayCount) {
                return this.FNonEmptyCashArray[index].FLeftTimeTicks;
            }
            else {
                // Время по индексу вычислимо только для бар-интервальной истории
                if (this.FPeriod > Periods.TIC) {
                    return this.CalculateTime(index);
                }
                else {
                    return NOT_FOUND_INTERVAL_VALUE;
                }
            }
        }
        else {
            return NOT_FOUND_INTERVAL_VALUE;
        } // UnknownTime
    }
    /// <summary>
    /// Получить вермя конца интервала по индексу
    /// </summary>
    GetCloseTime(index) {
        if (index >= 0 && index < this.FNonEmptyCashArrayCount) {
            if (index < this.FNonEmptyCashArrayCount) {
                return this.FNonEmptyCashArray[index].FRightTimeTicks;
            }
            else {
                // Время по индексу вычислимо только для бар-интервальной истории
                if (this.FPeriod > Periods.TIC) {
                    return this.CalculateTime(index) + BaseInterval.GetIntervalLength(this.FPeriod);
                }
                else {
                    return NOT_FOUND_INTERVAL_VALUE;
                }
            }
        }
        else {
            return NOT_FOUND_INTERVAL_VALUE;
        } // UnknownTime
    }
    GetInterval(index) {
        if (index >= 0 && index < this.FNonEmptyCashArrayCount) {
            return this.FNonEmptyCashArray[index];
        }
        else {
            return null;
        }
    }
    /// <summary>
    /// Получить версмя по индексу относительно первого бара
    /// </summary>
    CalculateTime(index) {
        return this.FStartTime + BaseInterval.GetIntervalLength(this.FPeriod) * index;
    }
    CalculateTimeLtRt(index, lt, rt) {
        const intervalLength = BaseInterval.GetIntervalLength(this.FPeriod);
        lt = this.FStartTime + intervalLength * index;
        rt = lt + intervalLength;
        return { lt, rt };
    }
    AddIndicator(indicator) {
        indicator.Parent = this;
        this.FFuncList.push(indicator);
        return indicator;
    }
    RemoveIndicator(indicator) {
        this.FFuncList.splice(this.FFuncList.indexOf(indicator), 1);
    }
    GetTimeToNext() {
        const useQuoteDelay = true;
        if (this.Period < 0) {
            // бары по тикам todo
            // let lastBar = this.GetInterval(this.Count - 1);
            // if (lastBar != null)
            //     return (string.Format("{0}t", -this.FPeriod - lastBar.QuoteCounter)).ToString();
        }
        else if (this.Period !== 0 && this.Period % Periods.RANGE !== 0) {
            const closeTime = this.GetCloseTime(this.FNonEmptyCashArrayCount - 1);
            let t = closeTime -
                Date.now() +
                (useQuoteDelay && this.Instrument != null ? this.Instrument.GetDelay(this.GetQuoteType()) : 0) *
                    TimeSpanPeriods.TicksPerMinute;
            // Нестыковочка по времени
            if (t < 0) {
                t += BaseInterval.GetIntervalLength(this.FPeriod);
                if (t < 0) {
                    // полная нестыковочка, десктоп в таких случаях не показывает TimeToNext - поступим так же
                    return '';
                }
            }
            const span = TimeSpan.ticksToTimeSpanObject(t);
            if (span.Days > 365) {
                return TimeSpan.ToTimeSpanString(span, TimeSpanFormat.YearDay);
            }
            else if (span.Days > 0) {
                return TimeSpan.ToTimeSpanString(span, TimeSpanFormat.DayHour);
            }
            else if (span.Hours > 0) {
                return TimeSpan.ToTimeSpanString(span, TimeSpanFormat.HourMinute);
            }
            else if (span.Minutes > 0) {
                return TimeSpan.ToTimeSpanString(span, TimeSpanFormat.MinuteSecond);
            }
            else {
                return TimeSpan.ToTimeSpanString(span);
            }
        }
        return '';
    }
    newQuote(message) {
        let needCallNextBar = false;
        if (!this.checkQuote(message)) {
            return;
        }
        if (!this.isRealTimeBuilding) {
            this.updateRealTimeBar(message);
        }
        if (!this.isRealTimeBuilding || this.collectTickQuotes) {
            this.quoteCache.newQuote(message);
            this.QuoteProcessed.Raise(this, message);
            return;
        }
        // Обработка котировки кешем
        if (this.FPeriod < 0) {
            // #region Тик-интервальная история
            // В тик-интервальной истории виртуальных дырок быть не должно
            // все дырки физические, та как невозможно поределить время виртуальной дырки
            // if (FNonEmptyCashArrayCount == 0)
            // {
            //    if (message.Code === MessageCode.CODE_INSTRUMENT_DAY_BAR) //не нужно создавать бар если приходит InstrumentDayBarMessage
            //    return;
            //    // Добавляем бар
            //    FNonEmptyCashArray.Add(new BaseInterval(message, FPeriod, Parent.serverTimeZone, HistoryType, this.spreadItem));
            //    FNonEmptyCashArrayCount = FNonEmptyCashArray.Count;
            //    FStartTime = FNonEmptyCashArray[0].FLeftTimeTicks;
            //    CallNextBar();
            //    OnHistoryExpanded(0, 1);
            // }
            // else
            // {
            //    // Получаем последний интервал
            //    BaseInterval interval = FNonEmptyCashArray[FNonEmptyCashArrayCount - 1];
            //    // Добавляем в него котировку
            //    int result = interval.Add(message, HistoryType, FPeriod, this.spreadItem);
            //    if (result > 0)
            //    {
            //        // Добавляем следующий интервал
            //        int oldLength = FNonEmptyCashArrayCount;
            //        BaseInterval newInterval = new BaseInterval(message, FPeriod, Parent.serverTimeZone, HistoryType, this.spreadItem);
            //        newInterval.SetNewBorders(interval.FRightTimeTicks, newInterval.FRightTimeTicks);
            //        FNonEmptyCashArray.Add(newInterval);
            //        FNonEmptyCashArrayCount = FNonEmptyCashArray.Count;
            //        CallNextBar();
            //        // Вызываем событие для вьюшек - история увеличилась еще на один бар
            //        OnHistoryExpanded(oldLength, FNonEmptyCashArrayCount);
            //    }
            // }
            // CallOnQuote();
            /// /this.QuoteProcessed.Raise(this,message)
            /// / Для периодов, составленных из тиков
            // return;
            // #endregion
        }
        if (this.FNonEmptyCashArrayCount === 0 || this.FPeriod === Periods.TIC) {
            // #region // Тиковая история или пустая
            const oldLength = this.FNonEmptyCashArrayCount;
            let workMsg = message;
            if (message.Code === Message.CODE_INSTRUMENT_DAY_BAR) {
                workMsg = message.GenerateQuoteMessage(this.HistoryType);
            }
            if (workMsg === null)
                return; // TODO проверить, нужен ли message ниже после этого if
            let interval;
            if (this.FPeriod === Periods.TIC && this.HistoryType !== HistoryType.QUOTE_TRADES) {
                interval = new BaseInterval();
                interval.FLeftTimeTicks = interval.FRightTimeTicks = workMsg.cTime.getTime();
                interval.LeftTime = interval.RightTime = workMsg.cTime;
                interval.Init(workMsg, this.HistoryType, this.FPeriod, this.spreadItem, this.Instrument);
            }
            else {
                const biInput = new BaseIntervalInputParams();
                biInput.instrument = this.Instrument;
                biInput.onlyMainSession = this.onlyMainSession;
                biInput.LeftTimeTicks = workMsg.cTime.getTime();
                interval = new BaseInterval(biInput, this.Period);
                interval.Init(workMsg, this.HistoryType, this.FPeriod, this.spreadItem, this.Instrument);
                // https://tp.traderevolution.com/entity/121924
                if (this.HistoryType === HistoryType.QUOTE_TRADES &&
                    message.Code === Message.CODE_QUOTE3 &&
                    this.FPeriod === Periods.DAY) {
                    const open = interval.Data[BaseInterval.OPEN_INDEX];
                    const tradeOpen = this.Instrument.InstrumentDayInfo.tradeOpen;
                    if (tradeOpen < open) {
                        interval.Data[BaseInterval.OPEN_INDEX] = tradeOpen;
                    }
                }
                //
                if (message.Code === Message.CODE_INSTRUMENT_DAY_BAR) {
                    const instrumentDayBarMessage = message;
                    if (instrumentDayBarMessage.Open != null) {
                        interval.Data[BaseInterval.OPEN_INDEX] = instrumentDayBarMessage.Open;
                    }
                }
            }
            const oneDayAggregation = Periods.TranslateToParsePeriod(this.FPeriod) === Periods.DAY;
            if (message.Code === Message.CODE_INSTRUMENT_DAY_BAR) {
                if (oneDayAggregation) {
                    this.FNonEmptyCashArray.push(interval);
                }
            }
            else {
                this.FNonEmptyCashArray.push(interval);
            }
            // if (this.FPeriod == Periods.TIC && this.HistoryType != HistoryType.QUOTE_TRADES)
            //    this.FNonEmptyCashArray.push(new BaseIntervalTick(message, this.FPeriod, this.Parent.serverTimeZone, this.HistoryType, this.spreadItem));
            // else
            //    this.FNonEmptyCashArray.push(new BaseInterval(message, this.FPeriod, this.Parent.serverTimeZone, this.HistoryType, this.spreadItem));
            this.FNonEmptyCashArrayCount = this.FNonEmptyCashArray.length;
            if (this.FNonEmptyCashArrayCount === 1) {
                this.FStartTime = this.FNonEmptyCashArray[0].FLeftTimeTicks;
            }
            needCallNextBar = true;
            // Вызываем событие для вьюшек - история увеличилась еще на один бар
            this.onHistoryExpanded(oldLength, this.FNonEmptyCashArrayCount);
            if (message.Code === Message.CODE_INSTRUMENT_DAY_BAR) {
                interval.correctBorders(this.FPeriod);
                if (oneDayAggregation) {
                    this.newQuote(message);
                }
            }
            // #endregion
        }
        else {
            // #region Время-интервальная (или бар-интервальная) история
            // Получаем последний интервал
            let interval = this.FNonEmptyCashArrayCount > 0 ? this.FNonEmptyCashArray[this.FNonEmptyCashArrayCount - 1] : null;
            let result = -1;
            if (interval == null) {
                if (message.Code === Message.CODE_INSTRUMENT_DAY_BAR) {
                    return;
                }
                // Последний интервал был пустым эмулируем его
                interval = new BaseInterval();
                let lt = 0;
                let rt = 0;
                // interval.Period = this.Period;
                const ltRtObj = this.CalculateTimeLtRt(this.FNonEmptyCashArrayCount - 1, lt, rt);
                lt = ltRtObj.lt;
                rt = ltRtObj.rt;
                interval.SetNewBorders(lt, rt);
                // Проверка, попадает ли котировка во временной интервал
                result = interval.Add(message, this.HistoryType, this.FPeriod, this.spreadItem, this.Parent.DataCache, this.Instrument);
                if (result > 0) {
                    // Время котировки принадрежит следующему интервалу - ничего не делаем,
                    // в истории остается дырка - а мы переходим на слддующий интервал (код ниже)
                    // ...
                }
                else if (result === 0) {
                    // время котировки принадлежит текущему интервалу
                    // Загоняем данные в интервал (они начальные для него - Add не подходит)
                    interval.Init(message, this.HistoryType, this.FPeriod, this.spreadItem, this.Instrument);
                    this.FNonEmptyCashArray.push(interval);
                    this.FNonEmptyCashArrayCount = this.FNonEmptyCashArray.length;
                }
            }
            else {
                result = interval.Add(message, this.HistoryType, this.FPeriod, this.spreadItem, this.Parent.DataCache, this.Instrument);
            }
            // Проверяем результат
            if (result > 0) {
                // Сохраняем предыдущую длину
                const oldLength = this.FNonEmptyCashArrayCount;
                const nextInterval = this.getNextIntervalByQuote(message, interval);
                if (!isNullOrUndefined(nextInterval)) {
                    this.FNonEmptyCashArray.push(nextInterval);
                    this.FNonEmptyCashArrayCount = this.FNonEmptyCashArray.length;
                    needCallNextBar = true;
                    // вызываем событие для вьюшек - история увеличилась на N-е количество баров
                    this.onHistoryExpanded(oldLength, this.FNonEmptyCashArrayCount);
                }
            }
            // #endregion
        }
        let indicator;
        let count = this.FFuncList.length;
        for (let k = 0; k < count; k++) {
            try {
                indicator = this.FFuncList[k];
                if (needCallNextBar) {
                    indicator.NextBar(true);
                }
                indicator.OnQuote(this, true);
            }
            catch (ex) {
                // чтобы 1 индюк не ломал все
                ErrorInformationStorage.GetException(ex);
                count = this.FFuncList.length;
            }
        }
        this.QuoteProcessed.Raise(this, message);
    }
    // #region True Range
    GetTrueRange(index) {
        const hi = this.GetByType(index, PriceType.High);
        const lo = this.GetByType(index, PriceType.Low);
        const prevClose = index > 0 ? this.GetByType(index - 1, PriceType.Close) : this.GetByType(index, PriceType.Close);
        const max = Math.max(hi - lo, Math.max(Math.abs(prevClose - hi), Math.abs(prevClose - lo)));
        return max;
    }
    // #endregion
    updateRealTimeBar(message) {
        if (isNullOrUndefined(this.realTimeBar)) {
            return;
        }
        const result = this.realTimeBar.Add(message, this.HistoryType, this.FPeriod, this.spreadItem, this.Parent.DataCache, this.Instrument);
        if (result <= 0) {
            return;
        }
        const nextInterval = this.getNextIntervalByQuote(message, this.realTimeBar);
        if (isNullOrUndefined(nextInterval)) {
            return;
        }
        this.realTimeBar = nextInterval;
    }
    getNextIntervalByQuote(message, interval) {
        // Получаем количество пустых интервалов, добавить
        let zeroes = 0;
        if (message.Type === HistoryType.QUOTE_LEVEL1) {
            zeroes = interval.CalculateNextInterv(message.Time, this.Parent.serverTimeZone, this.FPeriod, this.Instrument, !!this.onlyMainSession);
        }
        else if (message.Type === HistoryType.QUOTE_TRADES) {
            zeroes = interval.CalculateNextInterv(message.Time, this.Parent.serverTimeZone, this.FPeriod, this.Instrument, !!this.onlyMainSession);
        }
        // Получаем очередной интервал
        const nextInterval = interval.GetNextInterval(zeroes, this.HistoryType, this.FPeriod);
        // Проверка временных границ интервала с временем текущего тика (это алгоритмическая проверка )
        let valid = false;
        valid = nextInterval.Validate(message.Time);
        if (!valid) {
            return null;
        }
        // Инициализируем интервал
        nextInterval.Init(message, this.HistoryType, this.FPeriod, this.spreadItem, this.Instrument);
        return nextInterval;
    }
    checkQuote(message) {
        var _a, _b;
        // #90266, #100139 - фильтрация оносительно сессии
        if (this.onlyMainSession && message.SessionFlag && !SessionPeriod.IsMainType(message.SessionFlag)) {
            return false;
        }
        const trSess = message.TradingSession;
        if (((_a = this.Instrument) === null || _a === void 0 ? void 0 : _a.AllowFilterBarsBySession(this.FPeriod)) &&
            this.isFilterNotAllowedPeriods &&
            (trSess === null || trSess === void 0 ? void 0 : trSess.isBeforeOrAfterMarket())) {
            return false;
        }
        const basePeriod = Periods.TranslateToParsePeriod(this.FPeriod);
        if (((_b = this.Instrument) === null || _b === void 0 ? void 0 : _b.InstrumentDayBarMessageUpdateMode) &&
            basePeriod === Periods.DAY &&
            this.HistoryType === HistoryType.QUOTE_TRADES &&
            message.Type !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR) {
            return false;
        }
        return true;
    }
    onHistoryExpanded(oldLength, newLength) {
        try {
            this.HistoryExpanded.Raise(this, oldLength, newLength);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
    }
    applyQuotes() {
        const lastBar = this.FNonEmptyCashArrayCount > 0 ? this.FNonEmptyCashArray[this.FNonEmptyCashArrayCount - 1] : null;
        const lastQuoteId = lastBar !== null ? lastBar.LastQuoteId : null;
        const lastQuoteIdDate = lastBar !== null ? lastBar.LastQuoteIdDate : null;
        if (isNullOrUndefined(lastBar)) {
            return;
        }
        const quoteMessages = this.quoteCache.getAllQuotes();
        quoteMessages.forEach((quote) => {
            const quoteTimeTicks = quote.cTime.getTime();
            // 1 - время котировки меньше чем левая граница
            if (quoteTimeTicks < lastBar.FLeftTimeTicks) {
                return;
            }
            // 2 - кейс только для бар-интервальной истории - время котировки меньше чем последняя котировка записанная в бар
            if (lastQuoteIdDate !== null && quoteTimeTicks < lastQuoteIdDate) {
                return;
            }
            const quoteId = quote.QuoteUniqueID;
            // 3 - кейс для бар-интервальной истории или для тиковой истории (может быть несколько котировок в одну еденицу времени)
            if (lastQuoteId != null && quoteId != null && quoteId <= lastQuoteId) {
                return;
            }
            this.newQuote(quote);
        });
        const idbms = this.Instrument.GeneratedInstrumentDayBarMessages();
        idbms.forEach((idbm) => {
            this.newQuote(idbm);
        });
        this.quoteCache.clear();
    }
    updateRealTimeState() {
        if (this.FNonEmptyCashArrayCount === 0) {
            return;
        }
        const lastBar = this.FNonEmptyCashArray.at(-1);
        if (lastBar.isOpen) {
            this.isRealTimeBuilding = true;
        }
    }
    updateMaxLoadedState(historyParams) {
        if (historyParams.sortOrder === HistorySortOrder.Ascending) {
            return;
        }
        if (isValidNumber(historyParams.rightBarsCount) && !this.isRealTimeBuilding) {
            this.isMaxHistoryLoaded = this.lastLoadedBarsCount < historyParams.count + historyParams.rightBarsCount - 1; // minus 1 because of duplicate bar in the response
        }
        else if (isValidNumber(historyParams.count)) {
            this.isMaxHistoryLoaded = this.lastLoadedBarsCount < historyParams.count;
        }
        else {
            this.isMaxHistoryLoaded = this.StartTime < historyParams.FromTime.getTime();
        }
    }
    truncateToPeriodStart(time) {
        const date = new Date(time);
        const period = this.TimeFrameInfo.Periods;
        switch (period) {
            case Periods.DAY:
                date.setHours(0, 0, 0, 0);
                break;
            case Periods.MONTH:
                date.setDate(1);
                date.setHours(0, 0, 0, 0);
                break;
            case Periods.YEAR:
                date.setMonth(0, 1);
                date.setHours(0, 0, 0, 0);
                break;
        }
        return date;
    }
    /**
     * Create CashItem, Subscribe, LoadHistory
     * Use dispose to unsubscribe cashItem
     */
    static CreateWithHistory(instrument_1, historyParams_1) {
        return __awaiter(this, arguments, void 0, function* (instrument, historyParams, signal = null, subscribe = true, loadHistory = true, isNeedRealTime = true, oldCashItemWithSameParams = null) {
            var _a;
            // нужно добавить логику что если запрос приходит от предыщущего владельца
            // то нужно отменить предыдущий запрос (закрыть стрим)
            const qc = instrument === null || instrument === void 0 ? void 0 : instrument.DataCache.FQuoteCache;
            if (qc == null) {
                return null;
            }
            historyParams.Instrument = instrument;
            const timeFrameInfo = historyParams.TimeFrameInfo.Copy();
            const symbol = instrument.GetInteriorID();
            const cashItem = new CashItem(historyParams.Account, symbol, qc, timeFrameInfo, historyParams.isIgnoreAccount);
            if (subscribe) {
                cashItem.SubscribeCurrentInstrument();
            }
            if (!isNullOrUndefined(oldCashItemWithSameParams)) {
                const count = oldCashItemWithSameParams.Count() - 1;
                cashItem.realTimeBar =
                    (_a = oldCashItemWithSameParams.realTimeBar) !== null && _a !== void 0 ? _a : oldCashItemWithSameParams.GetInterval(count);
            }
            let ci = null;
            try {
                if (loadHistory) {
                    cashItem.collectTickQuotes = isNeedRealTime;
                    yield HistoryManager.reloadHistory(cashItem, historyParams, signal, isNeedRealTime);
                }
                ci = cashItem;
                ci.collectTickQuotes = false;
            }
            catch (_b) {
                ci = null;
            }
            if ((signal === null || signal === void 0 ? void 0 : signal.aborted) || ci == null) {
                ci === null || ci === void 0 ? void 0 : ci.Dispose();
                return null;
            }
            return ci;
        });
    }
}
